import React, { useEffect, useState, useRef } from 'react';
import { FileText, Music, Zap, Download, AlertTriangle, Play, Pause, Check, Shield } from 'lucide-react';
export function ClassifiedReveal() {
  const [revealStep, setRevealStep] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [waveHeights, setWaveHeights] = useState<number[]>(Array(30).fill(20));
  // DMT player state
  const [isDmtPlaying, setIsDmtPlaying] = useState(false);
  const [dmtProgress, setDmtProgress] = useState(0);
  const [dmtDuration, setDmtDuration] = useState(0);
  const [dmtCurrentTime, setDmtCurrentTime] = useState(0);
  const [dmtWaveHeights, setDmtWaveHeights] = useState<number[]>(Array(30).fill(20));
  const audioRef = useRef<HTMLAudioElement>(null);
  const dmtAudioRef = useRef<HTMLAudioElement>(null);
  const waveRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const dmtWaveRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const AUDIO_URL = 'https://share.deal.ai/files/6902f93c500a402c689c3020/d71e663b-2ff1-4919-8ed0-156f7315d3ca';
  const DMT_AUDIO_URL = 'https://share.deal.ai/files/6903cecd11c178905f921b0c/d87748c2-120b-4958-a11e-5ae4b9dda06b';
  useEffect(() => {
    const timeouts = [setTimeout(() => setRevealStep(1), 500), setTimeout(() => setRevealStep(2), 1200), setTimeout(() => setRevealStep(3), 1900), setTimeout(() => setRevealStep(4), 2600)];
    return () => timeouts.forEach(clearTimeout);
  }, []);
  // Waveform animation
  useEffect(() => {
    if (isPlaying) {
      waveRef.current = setInterval(() => {
        setWaveHeights(Array(30).fill(0).map(() => 15 + Math.random() * 85));
      }, 120);
    } else {
      if (waveRef.current) clearInterval(waveRef.current);
      setWaveHeights(Array(30).fill(0).map(() => 10 + Math.random() * 20));
    }
    return () => {
      if (waveRef.current) clearInterval(waveRef.current);
    };
  }, [isPlaying]);
  // DMT Waveform animation
  useEffect(() => {
    if (isDmtPlaying) {
      dmtWaveRef.current = setInterval(() => {
        setDmtWaveHeights(Array(30).fill(0).map(() => 15 + Math.random() * 85));
      }, 120);
    } else {
      if (dmtWaveRef.current) clearInterval(dmtWaveRef.current);
      setDmtWaveHeights(Array(30).fill(0).map(() => 10 + Math.random() * 20));
    }
    return () => {
      if (dmtWaveRef.current) clearInterval(dmtWaveRef.current);
    };
  }, [isDmtPlaying]);
  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    // Pause DMT if playing
    if (dmtAudioRef.current && isDmtPlaying) {
      dmtAudioRef.current.pause();
      setIsDmtPlaying(false);
    }
    if (isPlaying) {
      audio.pause();
    } else {
      audio.play();
    }
    setIsPlaying(!isPlaying);
  };
  const toggleDmtPlay = () => {
    const audio = dmtAudioRef.current;
    if (!audio) return;
    // Pause El Juego if playing
    if (audioRef.current && isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    }
    if (isDmtPlaying) {
      audio.pause();
    } else {
      audio.play();
    }
    setIsDmtPlaying(!isDmtPlaying);
  };
  const handleTimeUpdate = () => {
    const audio = audioRef.current;
    if (!audio) return;
    setCurrentTime(audio.currentTime);
    setProgress(audio.duration ? audio.currentTime / audio.duration * 100 : 0);
  };
  const handleLoadedMetadata = () => {
    const audio = audioRef.current;
    if (audio) setDuration(audio.duration);
  };
  const handleEnded = () => {
    setIsPlaying(false);
    setProgress(0);
    setCurrentTime(0);
  };
  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const audio = audioRef.current;
    if (!audio || !audio.duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = (e.clientX - rect.left) / rect.width;
    audio.currentTime = pct * audio.duration;
  };
  const handleDmtTimeUpdate = () => {
    const audio = dmtAudioRef.current;
    if (!audio) return;
    setDmtCurrentTime(audio.currentTime);
    setDmtProgress(audio.duration ? audio.currentTime / audio.duration * 100 : 0);
  };
  const handleDmtLoadedMetadata = () => {
    const audio = dmtAudioRef.current;
    if (audio) setDmtDuration(audio.duration);
  };
  const handleDmtEnded = () => {
    setIsDmtPlaying(false);
    setDmtProgress(0);
    setDmtCurrentTime(0);
  };
  const handleDmtProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const audio = dmtAudioRef.current;
    if (!audio || !audio.duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = (e.clientX - rect.left) / rect.width;
    audio.currentTime = pct * audio.duration;
  };
  const formatTime = (seconds: number) => {
    const min = Math.floor(seconds / 60);
    const sec = Math.floor(seconds % 60);
    return `${String(min).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
  };
  return <div className="min-h-screen w-full flex items-center justify-center px-3 py-8 md:p-8 relative z-20 overflow-hidden" data-id="element-14">
      <div className="max-w-4xl w-full border border-[#00ff41]/30 bg-black/90 backdrop-blur-md p-4 md:p-8 rounded-sm shadow-[0_0_30px_rgba(0,255,65,0.15)]" data-id="element-15">
        {/* Header */}
        <div className={`text-center mb-8 md:mb-10 transition-opacity duration-700 ${revealStep >= 1 ? 'opacity-100' : 'opacity-0'}`} data-id="element-16">
          <div className="inline-flex items-center gap-2 border border-red-500/50 px-2 md:px-3 py-1 mb-3 md:mb-4 bg-red-900/10" data-id="element-17">
            <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" data-id="element-18"></div>
            <span className="text-red-500 text-[10px] md:text-xs tracking-[0.15em] md:tracking-[0.2em] font-bold" data-id="element-19">
              TOP SECRET // EYES ONLY
            </span>
          </div>
          <h1 className="text-2xl md:text-4xl font-bold text-[#00ff41] mb-2 tracking-tighter" data-id="element-20">
            ACCESO CONCEDIDO
          </h1>
          <p className="text-[#008f11] text-xs md:text-base tracking-widest" data-id="element-21">
            ID: 742-91-X // NIVEL: 7
          </p>
        </div>

        {/* Files Grid */}
        <div className={`grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4 mb-6 md:mb-8 transition-all duration-700 ${revealStep >= 2 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`} data-id="element-22">
          <div className="flex flex-col p-3 md:p-4 border border-[#00ff41]/20 bg-[#00ff41]/5" data-id="element-23">
            <div className="flex items-center justify-between mb-2" data-id="element-24">
              <Music className="w-5 h-5 text-[#00ff41]" data-id="element-25" />
              <span className="text-[10px] text-[#008f11] border border-[#008f11] px-1" data-id="element-26">
                AUDIO
              </span>
            </div>
            <h3 className="text-xs md:text-sm font-bold text-[#00ff41] mb-1" data-id="element-27">
              LA_FRECUENCIA.wav
            </h3>
            <p className="text-[10px] md:text-xs text-gray-400 font-mono" data-id="element-28">
              Meditaciones guiadas + Binaurales 432Hz
            </p>
          </div>
          <div className="flex flex-col p-3 md:p-4 border border-[#00ff41]/20 bg-[#00ff41]/5" data-id="element-29">
            <div className="flex items-center justify-between mb-2" data-id="element-30">
              <FileText className="w-5 h-5 text-[#00ff41]" data-id="element-31" />
              <span className="text-[10px] text-[#008f11] border border-[#008f11] px-1" data-id="element-32">
                PDF
              </span>
            </div>
            <h3 className="text-xs md:text-sm font-bold text-[#00ff41] mb-1" data-id="element-33">
              EL_MANUAL.pdf
            </h3>
            <p className="text-[10px] md:text-xs text-gray-400 font-mono" data-id="element-34">
              Guía completa para salir de la Matrix
            </p>
          </div>
          <div className="flex flex-col p-3 md:p-4 border border-yellow-500/30 bg-yellow-500/5" data-id="element-35">
            <div className="flex items-center justify-between mb-2" data-id="element-36">
              <Zap className="w-5 h-5 text-yellow-500" data-id="element-37" />
              <span className="text-[10px] text-yellow-500 border border-yellow-500 px-1 animate-pulse" data-id="element-38">
                BONUS
              </span>
            </div>
            <h3 className="text-xs md:text-sm font-bold text-yellow-500 mb-1" data-id="element-39">
              PROTOCOLO_DMT.pdf
            </h3>
            <p className="text-[10px] md:text-xs text-gray-400 font-mono" data-id="element-40">
              Activación pineal sin sustancias
            </p>
          </div>
        </div>

        {/* Audio Player Preview */}
        <div className={`mb-8 md:mb-10 border border-[#00ff41]/30 bg-black p-3 md:p-4 transition-all duration-700 ${revealStep >= 3 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`} data-id="element-41">
          <audio ref={audioRef} src={AUDIO_URL} preload="metadata" crossOrigin="anonymous" onTimeUpdate={handleTimeUpdate} onLoadedMetadata={handleLoadedMetadata} onEnded={handleEnded} data-id="element-42" />
          <div className="flex items-center justify-between mb-3 text-[10px] md:text-xs text-[#008f11] font-mono" data-id="element-43">
            <span data-id="element-44">&gt; PREVIEW: EL_JUEGO_SAMPLE.wav</span>
            <span className={isPlaying ? 'animate-pulse text-[#00ff41]' : ''} data-id="element-45">
              {isPlaying ? '▶ PLAYING' : '■ PAUSED'}
            </span>
          </div>
          <div className="flex items-center gap-3" data-id="element-46">
            <button onClick={togglePlay} className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center border border-[#00ff41] text-[#00ff41] hover:bg-[#00ff41] hover:text-black transition-colors flex-shrink-0" data-id="element-47">
              {isPlaying ? <Pause className="w-4 h-4 md:w-5 md:h-5" data-id="element-48" /> : <Play className="w-4 h-4 md:w-5 md:h-5 ml-0.5" data-id="element-49" />}
            </button>
            <div className="flex-1 flex flex-col gap-2" data-id="element-50">
              <div className="h-8 flex items-end gap-[2px] overflow-hidden" data-id="element-51">
                {waveHeights.map((h, i) => <div key={i} className="flex-1 bg-[#00ff41]/60 rounded-t-sm transition-all duration-100" style={{
                height: `${h}%`
              }} data-id="element-52"></div>)}
              </div>
              <div className="relative h-1 bg-[#003b00] w-full cursor-pointer" onClick={handleProgressClick} data-id="element-53">
                <div className="absolute top-0 left-0 h-full bg-[#00ff41] transition-all duration-150" style={{
                width: `${progress}%`
              }} data-id="element-54"></div>
              </div>
            </div>
            <span className="text-[10px] md:text-xs text-[#008f11] font-mono flex-shrink-0" data-id="element-55">
              {formatTime(currentTime)} / {formatTime(duration || 0)}
            </span>
          </div>
        </div>

        {/* DMT Bonus Audio Player Preview */}
        <div className={`mb-8 md:mb-10 border border-yellow-500/30 bg-black p-3 md:p-4 transition-all duration-700 ${revealStep >= 3 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`} data-id="element-56">
          <audio ref={dmtAudioRef} src={DMT_AUDIO_URL} preload="metadata" crossOrigin="anonymous" onTimeUpdate={handleDmtTimeUpdate} onLoadedMetadata={handleDmtLoadedMetadata} onEnded={handleDmtEnded} data-id="element-57" />
          <div className="flex items-center justify-between mb-3 text-[10px] md:text-xs text-yellow-600 font-mono" data-id="element-58">
            <span data-id="element-59">&gt; PREVIEW: PROTOCOLO_DMT_SAMPLE.wav</span>
            <span className={isDmtPlaying ? 'animate-pulse text-yellow-500' : ''} data-id="element-60">
              {isDmtPlaying ? '▶ PLAYING' : '■ PAUSED'}
            </span>
          </div>
          <div className="flex items-center gap-3" data-id="element-61">
            <button onClick={toggleDmtPlay} className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center border border-yellow-500 text-yellow-500 hover:bg-yellow-500 hover:text-black transition-colors flex-shrink-0" data-id="element-62">
              {isDmtPlaying ? <Pause className="w-4 h-4 md:w-5 md:h-5" data-id="element-63" /> : <Play className="w-4 h-4 md:w-5 md:h-5 ml-0.5" data-id="element-64" />}
            </button>
            <div className="flex-1 flex flex-col gap-2" data-id="element-65">
              <div className="h-8 flex items-end gap-[2px] overflow-hidden" data-id="element-66">
                {dmtWaveHeights.map((h, i) => <div key={i} className="flex-1 bg-yellow-500/60 rounded-t-sm transition-all duration-100" style={{
                height: `${h}%`
              }} data-id="element-67"></div>)}
              </div>
              <div className="relative h-1 bg-yellow-900/40 w-full cursor-pointer" onClick={handleDmtProgressClick} data-id="element-68">
                <div className="absolute top-0 left-0 h-full bg-yellow-500 transition-all duration-150" style={{
                width: `${dmtProgress}%`
              }} data-id="element-69"></div>
              </div>
            </div>
            <span className="text-[10px] md:text-xs text-yellow-600 font-mono flex-shrink-0" data-id="element-70">
              {formatTime(dmtCurrentTime)} / {formatTime(dmtDuration || 0)}
            </span>
          </div>
        </div>

        {/* Pricing Packages */}
        <div className={`grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mb-6 md:mb-8 transition-all duration-700 ${revealStep >= 4 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`} data-id="element-71">
          {/* Complete Package - RECOMMENDED */}
          <div className="relative border-2 border-red-500 bg-red-500/5 p-5 md:p-6 flex flex-col shadow-[0_0_20px_rgba(255,0,0,0.15)] order-2 md:order-2" data-id="element-72">
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-black px-3 text-red-500 text-[10px] md:text-xs font-bold border border-red-500 tracking-widest whitespace-nowrap" data-id="element-73">
              ★ RECOMENDADO
            </div>
            <h3 className="text-lg md:text-xl font-bold text-red-500 mb-1 text-center mt-2" data-id="element-74">
              PAQUETE COMPLETO
            </h3>
            <p className="text-[10px] md:text-xs text-center text-gray-400 mb-5 font-mono" data-id="element-75">
              Audio + Libro + Meditaciones + Bonus DMT
            </p>
            <ul className="space-y-2 md:space-y-3 mb-6 flex-1" data-id="element-76">
              <li className="flex items-start gap-2 text-xs md:text-sm text-gray-300" data-id="element-77">
                <Check className="w-4 h-4 text-[#00ff41] mt-0.5 flex-shrink-0" data-id="element-78" />
                <span data-id="element-79">Audio Libro "El Juego"</span>
              </li>
              <li className="flex items-start gap-2 text-xs md:text-sm text-gray-300" data-id="element-80">
                <Check className="w-4 h-4 text-[#00ff41] mt-0.5 flex-shrink-0" data-id="element-81" />
                <span data-id="element-82">Libro PDF "El Despertar"</span>
              </li>
              <li className="flex items-start gap-2 text-xs md:text-sm text-gray-300" data-id="element-83">
                <Check className="w-4 h-4 text-[#00ff41] mt-0.5 flex-shrink-0" data-id="element-84" />
                <span data-id="element-85">Meditaciones Guiadas (30 días)</span>
              </li>
              <li className="flex items-start gap-2 text-xs md:text-sm text-gray-300" data-id="element-86">
                <Check className="w-4 h-4 text-[#00ff41] mt-0.5 flex-shrink-0" data-id="element-87" />
                <span data-id="element-88">Frecuencias Binaurales 432Hz</span>
              </li>
              <li className="flex items-start gap-2 text-xs md:text-sm text-yellow-500" data-id="element-89">
                <Check className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0" data-id="element-90" />
                <span data-id="element-91">BONUS: Protocolo DMT Pineal</span>
              </li>
            </ul>
            <div className="text-center mb-5" data-id="element-92">
              <span className="text-gray-500 line-through text-xs block mb-1" data-id="element-93">
                VALOR: $96.94
              </span>
              <span className="text-3xl font-bold text-red-500" style={{
              textShadow: '0 0 15px rgba(255,0,0,0.5)'
            }} data-id="element-94">
                $27
              </span>
              <span className="text-xs text-gray-400 block mt-1" data-id="element-95">
                USD — Acceso inmediato
              </span>
            </div>
            <a href="https://www.voicesoffreedomus.com/cart/45290003792058:1" target="_blank" rel="noopener noreferrer" className="w-full bg-red-600 text-white py-3 px-4 text-center font-bold uppercase tracking-widest hover:bg-red-500 transition-colors shadow-[0_0_20px_rgba(255,0,0,0.3)] flex items-center justify-center gap-2 text-sm md:text-base animate-pulse" data-id="element-96">
              <Download className="w-5 h-5" data-id="element-97" /> DESCARGAR TODO
            </a>
          </div>

          {/* Essential Package */}
          <div className="relative border border-[#00ff41]/40 bg-black/50 p-5 md:p-6 flex flex-col order-1 md:order-1" data-id="element-98">
            <h3 className="text-lg md:text-xl font-bold text-[#00ff41] mb-1 text-center mt-2" data-id="element-99">
              PAQUETE ESENCIAL
            </h3>
            <p className="text-[10px] md:text-xs text-center text-gray-500 mb-5 font-mono" data-id="element-100">
              Audio Libro + PDF + Bonus DMT
            </p>
            <ul className="space-y-2 md:space-y-3 mb-6 flex-1" data-id="element-101">
              <li className="flex items-start gap-2 text-xs md:text-sm text-gray-300" data-id="element-102">
                <Check className="w-4 h-4 text-[#00ff41] mt-0.5 flex-shrink-0" data-id="element-103" />
                <span data-id="element-104">Audio Libro "El Juego"</span>
              </li>
              <li className="flex items-start gap-2 text-xs md:text-sm text-gray-300" data-id="element-105">
                <Check className="w-4 h-4 text-[#00ff41] mt-0.5 flex-shrink-0" data-id="element-106" />
                <span data-id="element-107">Libro PDF "El Despertar"</span>
              </li>
              <li className="flex items-start gap-2 text-xs md:text-sm text-gray-500" data-id="element-108">
                <Check className="w-4 h-4 text-gray-600 mt-0.5 flex-shrink-0" data-id="element-109" />
                <span className="line-through opacity-50" data-id="element-110">
                  Meditaciones Guiadas
                </span>
              </li>
              <li className="flex items-start gap-2 text-xs md:text-sm text-gray-500" data-id="element-111">
                <Check className="w-4 h-4 text-gray-600 mt-0.5 flex-shrink-0" data-id="element-112" />
                <span className="line-through opacity-50" data-id="element-113">
                  Frecuencias Binaurales
                </span>
              </li>
              <li className="flex items-start gap-2 text-xs md:text-sm text-yellow-500" data-id="element-114">
                <Check className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0" data-id="element-115" />
                <span data-id="element-116">BONUS: Protocolo DMT Pineal</span>
              </li>
            </ul>
            <div className="text-center mb-5" data-id="element-117">
              <span className="text-gray-600 line-through text-xs block mb-1" data-id="element-118">
                VALOR: $47
              </span>
              <span className="text-2xl font-bold text-[#00ff41]" data-id="element-119">$17</span>
              <span className="text-xs text-gray-500 block mt-1" data-id="element-120">
                USD — Acceso inmediato
              </span>
            </div>
            <a href="https://www.voicesoffreedomus.com/cart/45280183124154:1" target="_blank" rel="noopener noreferrer" className="w-full bg-transparent border border-[#00ff41] text-[#00ff41] py-3 px-4 text-center font-bold uppercase tracking-widest hover:bg-[#00ff41]/10 transition-colors flex items-center justify-center gap-2 text-sm md:text-base" data-id="element-121">
              <FileText className="w-5 h-5" data-id="element-122" /> ACCEDER
            </a>
          </div>
        </div>

        {/* Guarantee + Warning */}
        <div className="text-center space-y-3" data-id="element-123">
          <div className="flex items-center justify-center gap-2 text-[10px] md:text-xs text-[#008f11]" data-id="element-124">
            <Shield className="w-3 h-3 md:w-4 md:h-4" data-id="element-125" />
            <span data-id="element-126">
              30 días de garantía. Si no despiertas, te devolvemos cada centavo.
            </span>
          </div>
          <div className="flex items-start justify-center gap-2 text-[10px] md:text-xs text-red-400 max-w-md mx-auto opacity-80" data-id="element-127">
            <AlertTriangle className="w-3 h-3 md:w-4 md:h-4 flex-shrink-0 mt-0.5" data-id="element-128" />
            <p data-id="element-129">
              ADVERTENCIA: El conocimiento contenido en estos archivos es
              irreversible.
            </p>
          </div>
        </div>
      </div>
    </div>;
}